import { defineStore } from 'pinia';

import { CompanyRole } from '@tallo/investor/company';
import { AddressDto } from '@tallo/property';
import { UserDto } from '@tallo/user';

import { InvestorDto, InvestorOnboardingDto } from '../dtos/investor.dto';
import { UpdateInvestorPreferencesDto } from '../dtos/update-investor-preferences.dto';
import { UpdateInvestorUserDto } from '../dtos/update-investor-user.dto';
import investorUserService from '../services/investor-user.service';
import investorService from '../services/investor.service';

export const useInvestorStore = defineStore('globalStore', {
  state: (): {
    investor: InvestorDto | undefined;
  } => ({
    investor: undefined,
  }),
  getters: {
    address(): AddressDto {
      return {
        address: this.investor?.address || '',
        city: this.investor?.city || '',
        state: this.investor?.state || '',
        zip: Number(this.investor?.zip) || 0,
        latitude: this.investor?.latitude || 0,
        longitude: this.investor?.longitude || 0,
      };
    },
    /** Admins run the company: the portfolios' order is theirs to set, and any unit is theirs to change */
    isAdmin(): boolean {
      return this.investor?.role === CompanyRole.ADMIN;
    },
    needsInitialOnboarding(): boolean {
      return this.investor?.onboarding?.initialOnboardingCompleted !== true;
    },
    needsPropertyOnboarding(): boolean {
      return this.investor?.onboarding?.propertyOnboardingCompleted !== true;
    },
  },
  actions: {
    async load(): Promise<void> {
      this.investor = await investorService.getInvestor();
    },

    async updateAddress(address: AddressDto): Promise<void> {
      this.investor = await investorService.updateAddress(address);
    },

    async updatePreferences(dto: UpdateInvestorPreferencesDto): Promise<void> {
      this.investor = await investorService.updatePreferences(dto);
    },

    async updateUser(userData: UpdateInvestorUserDto): Promise<void> {
      const updatedUser = await investorUserService.updateUser(userData);
      this.setUser(updatedUser);
    },

    /** Sync the cached user after profile endpoints that return the fresh UserDto */
    setUser(user: UserDto): void {
      if (this.investor) {
        this.investor.user = user;
      }
    },

    /** Mirrors a successful completion call so the wizard can finish even if the refresh fails */
    markInitialOnboardingCompleted(): void {
      this.markOnboardingCompleted('initialOnboardingCompleted');
    },

    /** Mirrors a completion call locally so its gate closes before (or without) the refresh */
    markOnboardingCompleted(flag: keyof InvestorOnboardingDto): void {
      if (this.investor) {
        this.investor.onboarding = { ...this.investor.onboarding, [flag]: true };
      }
    },
  },
});
